// EnvironmentEditor — six-tab environment editor used in the Workbench
// centre pane. Every editable scalar is a <ParamField>; mode pills drive
// which sub-section gates which branch of the physics kernel. Three of
// the tabs carry a live Plotly preview sampled from the backend:
//
//   * Wind         — wind(z) curve from 0 to 500 m
//   * Turbulence   — one-sided Dryden / von-Kármán PSD (log-log)
//   * Solar        — elevation + azimuth polar arc for the lat/lon/date
//
// The gust timeline is a simple ordered editor, no Plotly; a spark plot
// of the gust envelope |gust|(t) renders inline when gusts exist.
//
// Every mutation is patched through `sim.environments.patch` with the
// narrowest possible partial (dot-paths inside the flattener).
// Debounced 300 ms so rapid typing doesn't hammer the API.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import ParamField from "../ParamField";
import SpinnerButton from "../SpinnerButton";
import { toast } from "sonner";
import { sim } from "@/api/endpoints";
import { formatApiError } from "@/lib/formatApiError";

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------
function PF({ env, path, label, kind, description, bounds, onChange, readOnly }) {
    // Walk a dot-path on `env` to find the Parameter node (or null).
    const node = path.split(".").reduce((acc, k) => (acc ? acc[k] : null), env);
    return (
        <ParamField
            id={`env-${path.replace(/\./g, "-")}`}
            testId={`env-${path.replace(/\./g, "-")}`}
            label={label}
            description={description}
            kind={kind}
            value={node ? node.value : null}
            source={node?.source}
            confidence={node?.confidence}
            citation={node?.citation}
            bounds={node?.bounds || bounds}
            readOnly={readOnly}
            onChange={(si, unit) => onChange?.(path, si, unit)}
        />
    );
}

function ModePill({ active, onClick, testId, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            aria-pressed={active}
            className={`text-[11px] font-mono uppercase tracking-wider px-3 py-1 rounded-full border transition-colors ${
                active
                    ? "bg-ux-accent text-ux-bg-1 border-ux-accent"
                    : "bg-ux-bg-2 text-ux-text-2 border-ux-border-1 hover:border-ux-accent/60"
            }`}
        >
            {children}
        </button>
    );
}

// ---------------------------------------------------------------------
// Dynamic Plotly — same pattern as AeroTableEditor, gated on the env id.
// ---------------------------------------------------------------------
function usePlotly(containerRef, deps, buildTraces) {
    const [err, setErr] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!containerRef.current) return;
            try {
                const traces = buildTraces();
                if (!traces || traces.length === 0) return;
                const Plotly = (await import("plotly.js-dist-min")).default;
                if (cancelled || !containerRef.current) return;
                const layout = traces._layout || {};
                const cfg = { responsive: true, displaylogo: false };
                await Plotly.newPlot(containerRef.current, traces, layout, cfg);
                setErr(null);
            } catch (e) {
                if (!cancelled) setErr(e?.message || String(e));
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return err;
}

// ---------------------------------------------------------------------
// Individual tabs
// ---------------------------------------------------------------------
function AtmosphereTab({ env, onField, onMode, readOnly }) {
    const mode = env?.atmosphere?.mode || "isa";
    return (
        <div className="space-y-4" data-testid="env-atmosphere-tab">
            <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-wider text-ux-text-3">Mode</span>
                <ModePill
                    active={mode === "isa"}
                    onClick={() => onMode("atmosphere.mode", "isa")}
                    testId="env-atmosphere-mode-isa"
                >
                    ISA
                </ModePill>
                <ModePill
                    active={mode === "non_standard"}
                    onClick={() => onMode("atmosphere.mode", "non_standard")}
                    testId="env-atmosphere-mode-non-standard"
                >
                    Non-standard
                </ModePill>
                <ModePill
                    active={mode === "density_altitude"}
                    onClick={() => onMode("atmosphere.mode", "density_altitude")}
                    testId="env-atmosphere-mode-density-altitude"
                >
                    Density-alt
                </ModePill>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <PF env={env} path="atmosphere.reference_altitude" label="Reference altitude" kind="length" onChange={onField} readOnly={readOnly} />
                <PF env={env} path="atmosphere.T_offset_K" label="Temperature offset" kind="number" description="Added to ISA T(h)" onChange={onField} readOnly={readOnly || mode === "isa" || mode === "density_altitude"} />
                <PF env={env} path="atmosphere.P_offset_Pa" label="Pressure offset" kind="number" description="Added to ISA P(h)" onChange={onField} readOnly={readOnly || mode === "isa" || mode === "density_altitude"} />
                <PF env={env} path="atmosphere.density_altitude_m" label="Density altitude" kind="length" description="Only consulted when Mode = Density-alt" onChange={onField} readOnly={readOnly || mode !== "density_altitude"} />
            </div>
        </div>
    );
}

function WindTab({ env, envId, onField, onMode, readOnly, plotTick }) {
    const mode = env?.wind?.mode || "constant";
    const [rows, setRows] = useState([]);
    const plotRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        if (!envId) return undefined;
        (async () => {
            try {
                const r = await sim.environments.windProfile(envId, 0, 500, 64);
                if (!cancelled) setRows(r.rows || []);
            } catch {
                if (!cancelled) setRows([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [envId, plotTick]);

    const plotErr = usePlotly(plotRef, [rows.length, mode], () => {
        if (rows.length === 0) return [];
        const traces = [
            {
                x: rows.map((r) => r.speed_mps),
                y: rows.map((r) => r.height_m),
                mode: "lines+markers",
                type: "scatter",
                line: { color: "#14b8a6", width: 2 },
                marker: { size: 4 },
                name: "wind(z)",
            },
        ];
        traces._layout = {
            margin: { l: 48, r: 20, t: 10, b: 38 },
            xaxis: { title: "speed (m/s)", zeroline: false, gridcolor: "#233" },
            yaxis: { title: "height (m)", gridcolor: "#233" },
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            font: { color: "#cbd5e1", family: "ui-monospace, monospace", size: 11 },
            height: 240,
        };
        return traces;
    });

    return (
        <div className="space-y-4" data-testid="env-wind-tab">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] uppercase tracking-wider text-ux-text-3">Mode</span>
                <ModePill active={mode === "constant"} onClick={() => onMode("wind.mode", "constant")} testId="env-wind-mode-constant">Constant</ModePill>
                <ModePill active={mode === "log_law"} onClick={() => onMode("wind.mode", "log_law")} testId="env-wind-mode-log-law">Log-law</ModePill>
                <ModePill active={mode === "power_law"} onClick={() => onMode("wind.mode", "power_law")} testId="env-wind-mode-power-law">Power-law</ModePill>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <PF env={env} path="wind.reference_height_m" label="Reference height" kind="length" onChange={onField} readOnly={readOnly} />
                <PF env={env} path="wind.reference_speed_mps" label="Reference speed" kind="speed" onChange={onField} readOnly={readOnly} />
                <PF env={env} path="wind.direction_deg_true" label="Direction (from, °true)" kind="number" onChange={onField} readOnly={readOnly} />
                <PF env={env} path="wind.roughness_length_m" label="Roughness length z₀" kind="length" description="Log-law only" onChange={onField} readOnly={readOnly || mode !== "log_law"} />
                <PF env={env} path="wind.shear_exponent_alpha" label="Shear exponent α" kind="number" description="Power-law only" onChange={onField} readOnly={readOnly || mode !== "power_law"} />
            </div>
            <div className="rounded border border-ux-border-1 bg-ux-bg-2/60 p-3">
                <div className="text-[11px] uppercase tracking-wider text-ux-text-3 mb-1">Wind profile preview</div>
                {plotErr ? (
                    <div className="text-[12px] text-ux-text-3" data-testid="env-wind-plot-error">
                        Plot unavailable ({plotErr}).
                    </div>
                ) : (
                    <div ref={plotRef} data-testid="env-wind-plot" className="w-full min-h-[240px]" />
                )}
            </div>
        </div>
    );
}

function TurbulenceTab({ env, envId, onField, onMode, readOnly, plotTick }) {
    const mode = env?.turbulence?.mode || "none";
    const [rows, setRows] = useState([]);
    const plotRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        if (!envId || mode === "none") {
            setRows([]);
            return undefined;
        }
        (async () => {
            try {
                const r = await sim.environments.turbulencePsd(envId, {
                    airspeed_mps: 50,
                    f_min: 0.01,
                    f_max: 10,
                    n: 96,
                });
                if (!cancelled) setRows(r.rows || []);
            } catch {
                if (!cancelled) setRows([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [envId, mode, plotTick]);

    const plotErr = usePlotly(plotRef, [rows.length], () => {
        if (rows.length === 0) return [];
        const traces = [
            { x: rows.map((r) => r.freq_hz), y: rows.map((r) => r.Suu), mode: "lines", name: "Sᵤᵤ", line: { color: "#14b8a6", width: 2 } },
            { x: rows.map((r) => r.freq_hz), y: rows.map((r) => r.Svv), mode: "lines", name: "Sᵥᵥ", line: { color: "#a78bfa", width: 2 } },
            { x: rows.map((r) => r.freq_hz), y: rows.map((r) => r.Sww), mode: "lines", name: "Sww", line: { color: "#fb923c", width: 2 } },
        ];
        traces._layout = {
            margin: { l: 56, r: 20, t: 10, b: 40 },
            xaxis: { title: "freq (Hz)", type: "log", gridcolor: "#233" },
            yaxis: { title: "PSD", type: "log", gridcolor: "#233" },
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            font: { color: "#cbd5e1", family: "ui-monospace, monospace", size: 11 },
            legend: { orientation: "h", y: 1.08 },
            height: 260,
        };
        return traces;
    });

    return (
        <div className="space-y-4" data-testid="env-turbulence-tab">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] uppercase tracking-wider text-ux-text-3">Mode</span>
                <ModePill active={mode === "none"} onClick={() => onMode("turbulence.mode", "none")} testId="env-turb-mode-none">None</ModePill>
                <ModePill active={mode === "dryden"} onClick={() => onMode("turbulence.mode", "dryden")} testId="env-turb-mode-dryden">Dryden</ModePill>
                <ModePill active={mode === "von_karman"} onClick={() => onMode("turbulence.mode", "von_karman")} testId="env-turb-mode-von-karman">von Kármán</ModePill>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <PF env={env} path="turbulence.intensity_mps" label="σᵤ intensity" kind="speed" onChange={onField} readOnly={readOnly || mode === "none"} />
                <PF env={env} path="turbulence.length_scale_m" label="Lᵤ length scale" kind="length" onChange={onField} readOnly={readOnly || mode === "none"} />
                <PF env={env} path="turbulence.sigma_v_over_u" label="σᵥ / σᵤ" kind="number" onChange={onField} readOnly={readOnly || mode === "none"} />
                <PF env={env} path="turbulence.sigma_w_over_u" label="σw / σᵤ" kind="number" onChange={onField} readOnly={readOnly || mode === "none"} />
            </div>
            <div className="rounded border border-ux-border-1 bg-ux-bg-2/60 p-3">
                <div className="text-[11px] uppercase tracking-wider text-ux-text-3 mb-1">PSD (log-log)</div>
                {mode === "none" ? (
                    <div className="text-[12px] text-ux-text-3">No turbulence selected.</div>
                ) : plotErr ? (
                    <div className="text-[12px] text-ux-text-3" data-testid="env-turb-plot-error">
                        Plot unavailable ({plotErr}).
                    </div>
                ) : (
                    <div ref={plotRef} data-testid="env-turb-plot" className="w-full min-h-[260px]" />
                )}
            </div>
        </div>
    );
}

function GustsTab({ env, envId, onReplaceGusts, readOnly }) {
    const gusts = useMemo(() => env?.gusts || [], [env]);
    const [rows, setRows] = useState(gusts);
    useEffect(() => setRows(gusts), [gusts]);

    const persist = (next) => {
        setRows(next);
        if (!envId) return;
        sim.environments.patch(envId, { gusts: next }).catch(() => {});
    };

    const addRow = () => {
        const next = [
            ...rows,
            { t_start_s: (rows.length + 1) * 5.0, duration_s: 2.0, ramp_s: 0.5, vector_mps: { u: 0, v: 3, w: 0 } },
        ];
        persist(next);
    };
    const removeRow = (idx) => persist(rows.filter((_, i) => i !== idx));
    const updateRow = (idx, path, value) => {
        const copy = rows.map((r, i) => (i === idx ? JSON.parse(JSON.stringify(r)) : r));
        const parts = path.split(".");
        let cursor = copy[idx];
        for (let p = 0; p < parts.length - 1; p += 1) cursor = cursor[parts[p]];
        cursor[parts[parts.length - 1]] = Number.isFinite(Number(value)) ? Number(value) : value;
        persist(copy);
    };

    return (
        <div className="space-y-3" data-testid="env-gusts-tab">
            <div className="flex items-center justify-between">
                <div className="text-[12px] text-ux-text-2">
                    Body-frame gusts applied additively on top of the base wind.
                </div>
                <Button size="sm" variant="outline" onClick={addRow} disabled={readOnly} data-testid="env-gust-add">
                    + Add gust
                </Button>
            </div>
            {rows.length === 0 ? (
                <div className="rounded border border-dashed border-ux-border-1 bg-ux-bg-2/40 p-4 text-[12px] text-ux-text-3" data-testid="env-gusts-empty">
                    No gusts scheduled.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-[12px] font-mono" data-testid="env-gusts-table">
                        <thead className="text-ux-text-3">
                            <tr>
                                {["t_start", "duration", "ramp", "u", "v", "w", ""].map((h) => (
                                    <th key={h} className="text-left font-normal px-2 py-1 border-b border-ux-border-1">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((g, i) => (
                                <tr key={i} data-testid={`env-gust-row-${i}`} className="border-b border-ux-border-1/60">
                                    {[
                                        ["t_start_s", g.t_start_s],
                                        ["duration_s", g.duration_s],
                                        ["ramp_s", g.ramp_s],
                                        ["vector_mps.u", g.vector_mps?.u ?? 0],
                                        ["vector_mps.v", g.vector_mps?.v ?? 0],
                                        ["vector_mps.w", g.vector_mps?.w ?? 0],
                                    ].map(([k, v]) => (
                                        <td key={k} className="px-2 py-1">
                                            <Input
                                                type="number"
                                                step="0.1"
                                                value={v ?? 0}
                                                readOnly={readOnly}
                                                className="w-20 h-7"
                                                onChange={(e) => updateRow(i, k, e.target.value)}
                                                data-testid={`env-gust-${i}-${k.replace(/\./g, "-")}`}
                                            />
                                        </td>
                                    ))}
                                    <td className="px-2 py-1">
                                        <Button size="sm" variant="ghost" onClick={() => removeRow(i)} disabled={readOnly} data-testid={`env-gust-remove-${i}`}>
                                            ✕
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function VisibilityTab({ env, onField, onMode, readOnly }) {
    const precip = env?.visibility?.precipitation_type || "none";
    return (
        <div className="space-y-4" data-testid="env-visibility-tab">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] uppercase tracking-wider text-ux-text-3">Precipitation</span>
                {["none", "rain", "snow", "sleet"].map((p) => (
                    <ModePill
                        key={p}
                        active={precip === p}
                        onClick={() => onMode("visibility.precipitation_type", p)}
                        testId={`env-precip-${p}`}
                    >
                        {p}
                    </ModePill>
                ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <PF env={env} path="visibility.visibility_m" label="Visibility" kind="length" onChange={onField} readOnly={readOnly} />
                <PF env={env} path="visibility.fog_density_per_m" label="Fog density (1/m)" kind="number" onChange={onField} readOnly={readOnly} />
                <PF env={env} path="visibility.precipitation_rate_mm_per_h" label="Precip rate" kind="number" onChange={onField} readOnly={readOnly || precip === "none"} />
                <PF env={env} path="visibility.EO_attenuation_coeff_per_m" label="EO attenuation (1/m)" kind="number" onChange={onField} readOnly={readOnly} />
                <PF env={env} path="visibility.IR_attenuation_coeff_per_m" label="IR attenuation (1/m)" kind="number" onChange={onField} readOnly={readOnly} />
            </div>
        </div>
    );
}

function SolarTab({ env, envId, onField, onDatetime, readOnly, plotTick }) {
    const [angles, setAngles] = useState(null);
    const plotRef = useRef(null);
    const iso = env?.solar?.datetime_utc || "";
    useEffect(() => {
        let cancelled = false;
        if (!envId) return undefined;
        (async () => {
            try {
                const a = await sim.environments.solar(envId, iso || undefined);
                if (!cancelled) setAngles(a);
            } catch {
                if (!cancelled) setAngles(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [envId, iso, plotTick]);

    const plotErr = usePlotly(plotRef, [angles?.elevation_deg, angles?.azimuth_deg], () => {
        if (!angles) return [];
        const az = angles.azimuth_deg || 0;
        const el = angles.elevation_deg || 0;
        const traces = [
            {
                type: "scatterpolar",
                r: [90, Math.max(0, 90 - el)],
                theta: [az, az],
                mode: "lines+markers",
                line: { color: "#fb923c", width: 3 },
                marker: { size: [8, 14], color: ["#14b8a6", "#fbbf24"] },
                name: "sun",
            },
        ];
        traces._layout = {
            margin: { l: 0, r: 0, t: 10, b: 10 },
            polar: {
                radialaxis: { visible: false, range: [0, 90] },
                angularaxis: { direction: "clockwise", rotation: 90, gridcolor: "#233" },
                bgcolor: "transparent",
            },
            showlegend: false,
            paper_bgcolor: "transparent",
            font: { color: "#cbd5e1", family: "ui-monospace, monospace", size: 11 },
            height: 240,
        };
        return traces;
    });

    return (
        <div className="space-y-4" data-testid="env-solar-tab">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <label className="flex flex-col gap-1 text-[12px]">
                    <span className="text-ux-text-3 uppercase tracking-wider text-[11px]">UTC datetime</span>
                    <Input
                        type="datetime-local"
                        value={iso ? iso.replace("Z", "").slice(0, 16) : ""}
                        readOnly={readOnly}
                        onChange={(e) => onDatetime(`${e.target.value}:00Z`)}
                        data-testid="env-solar-datetime"
                    />
                </label>
                <PF env={env} path="solar.latitude_deg" label="Latitude (°)" kind="number" onChange={onField} readOnly={readOnly} />
                <PF env={env} path="solar.longitude_deg" label="Longitude (°)" kind="number" onChange={onField} readOnly={readOnly} />
            </div>
            <div className="rounded border border-ux-border-1 bg-ux-bg-2/60 p-3">
                <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-wider text-ux-text-3">Sun position</div>
                    {angles && (
                        <div className="text-[12px] text-ux-text-2 font-mono" data-testid="env-solar-readout">
                            elev {angles.elevation_deg?.toFixed(1)}° · az {angles.azimuth_deg?.toFixed(1)}°
                        </div>
                    )}
                </div>
                {plotErr ? (
                    <div className="text-[12px] text-ux-text-3 mt-1" data-testid="env-solar-plot-error">
                        Plot unavailable ({plotErr}).
                    </div>
                ) : (
                    <div ref={plotRef} data-testid="env-solar-plot" className="w-full min-h-[240px]" />
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------
// Root editor
// ---------------------------------------------------------------------
export default function EnvironmentEditor({ environment, readOnly = false, onChange }) {
    const [env, setEnv] = useState(environment);
    const [plotTick, setPlotTick] = useState(0);
    const debounceRef = useRef(null);

    useEffect(() => setEnv(environment), [environment]);

    const envId = env?.id;

    const patch = useCallback(
        (body) => {
            if (!envId) return;
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(async () => {
                try {
                    const fresh = await sim.environments.patch(envId, body);
                    setEnv(fresh);
                    setPlotTick((t) => t + 1);
                    onChange?.(fresh);
                } catch (e) {
                    toast.error(formatApiError(e, "Failed to save environment"));
                }
            }, 300);
        },
        [envId, onChange],
    );

    // Update a Parameter leaf at a dot-path like "wind.reference_speed_mps".
    const onField = (path, value, unit) => {
        const [section, field] = path.split(".");
        const prev = (env?.[section]?.[field] || {});
        const next = { ...prev, value, unit: unit || prev.unit, source: "user" };
        patch({ [section]: { [field]: next } });
        // Optimistic local update so the UI doesn't flicker between the
        // edit and the debounced save.
        setEnv((e) => ({
            ...e,
            [section]: { ...(e?.[section] || {}), [field]: next },
        }));
    };
    // Update a non-Parameter scalar on a section (mode, precip type, …).
    const onMode = (path, value) => {
        const [section, key] = path.split(".");
        patch({ [section]: { [key]: value } });
        setEnv((e) => ({ ...e, [section]: { ...(e?.[section] || {}), [key]: value } }));
    };
    const onDatetime = (iso) => {
        patch({ solar: { datetime_utc: iso } });
        setEnv((e) => ({ ...e, solar: { ...(e?.solar || {}), datetime_utc: iso } }));
    };

    if (!env) {
        return <div className="text-[13px] text-ux-text-3" data-testid="env-editor-empty">No environment attached.</div>;
    }

    return (
        <div className="space-y-4" data-testid="environment-editor">
            <header className="flex items-center justify-between">
                <div>
                    <h3 className="text-[14px] font-medium text-ux-text-1" data-testid="env-editor-name">
                        {env.name}
                    </h3>
                    <p className="text-[12px] text-ux-text-3">{env.notes || "No notes."}</p>
                </div>
                <div className="flex items-center gap-2">
                    <span
                        className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border border-ux-border-1 bg-ux-bg-2"
                        data-testid="env-editor-source"
                    >
                        {env.is_preset ? "preset" : "tenant"}
                    </span>
                    <SpinnerButton
                        size="sm"
                        variant="outline"
                        onClick={() => setPlotTick((t) => t + 1)}
                        testId="env-editor-refresh"
                    >
                        Refresh previews
                    </SpinnerButton>
                </div>
            </header>

            <Tabs defaultValue="atmosphere">
                <TabsList>
                    <TabsTrigger value="atmosphere" data-testid="env-tab-atmosphere">Atmosphere</TabsTrigger>
                    <TabsTrigger value="wind" data-testid="env-tab-wind">Wind</TabsTrigger>
                    <TabsTrigger value="turbulence" data-testid="env-tab-turbulence">Turbulence</TabsTrigger>
                    <TabsTrigger value="gusts" data-testid="env-tab-gusts">Gusts</TabsTrigger>
                    <TabsTrigger value="visibility" data-testid="env-tab-visibility">Visibility</TabsTrigger>
                    <TabsTrigger value="solar" data-testid="env-tab-solar">Solar</TabsTrigger>
                </TabsList>
                <TabsContent value="atmosphere" className="mt-4">
                    <AtmosphereTab env={env} onField={onField} onMode={onMode} readOnly={readOnly} />
                </TabsContent>
                <TabsContent value="wind" className="mt-4">
                    <WindTab env={env} envId={envId} onField={onField} onMode={onMode} readOnly={readOnly} plotTick={plotTick} />
                </TabsContent>
                <TabsContent value="turbulence" className="mt-4">
                    <TurbulenceTab env={env} envId={envId} onField={onField} onMode={onMode} readOnly={readOnly} plotTick={plotTick} />
                </TabsContent>
                <TabsContent value="gusts" className="mt-4">
                    <GustsTab env={env} envId={envId} readOnly={readOnly} />
                </TabsContent>
                <TabsContent value="visibility" className="mt-4">
                    <VisibilityTab env={env} onField={onField} onMode={onMode} readOnly={readOnly} />
                </TabsContent>
                <TabsContent value="solar" className="mt-4">
                    <SolarTab env={env} envId={envId} onField={onField} onDatetime={onDatetime} readOnly={readOnly} plotTick={plotTick} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
